import { Carrier } from "../../types/clientTab";

export interface BrokerBonusSummary {
  brokerId: string;

  guardianNewBonusLevelOne: number;
  guardianNewBonusLevelTwo: number;
  guardianNewBonusLevelThree: number;
  guardianPersistencyBonusLevelOne: number;
  guardianPersistencyBonusLevelTwo: number;
  guardianPersistencyBonusLevelThree: number;
  guardianPlanCount: number;
  guardianClientCount: number;

  principalNewBonusLevelOne: number;
  principalNewBonusLevelTwo: number;
  principalNewBonusLevelThree: number;
  principalPersistencyBonusLevelOne: number;
  principalPersistencyBonusLevelTwo: number;
  principalPersistencyBonusLevelThree: number;
  principalPlanCount: number;
  principalClientCount: number;

  guardianDentalAnnualPremium: number;
  guardianVisionAnnualPremium: number;
  guardianLifeAnnualPremium: number;
  guardianDisabilityAnnualPremium: number;
  guardianTotalAnnualPremium: number;

  principalDentalAnnualPremium: number;
  principalVisionAnnualPremium: number;
  principalLifeAnnualPremium: number;
  principalDisabilityAnnualPremium: number;
  principalTotalAnnualPremium: number;

  totalNewBonusLevelOne: number;
  totalNewBonusLevelTwo: number;
  totalNewBonusLevelThree: number;
  totalPersistencyBonusLevelOne: number;
  totalPersistencyBonusLevelTwo: number;
  totalPersistencyBonusLevelThree: number;
  totalPlanCount: number;
  totalClientCount: number;
  totalBonus: number;

  totalDentalAnnualPremium: number;
  totalVisionAnnualPremium: number;
  totalLifeAnnualPremium: number;
  totalDisabilityAnnualPremium: number;
  totalAnnualPremium: number;
}

export interface BBEBroker {
  name: string;
  salesforceId: string;
}

export interface BBEOutletContext {
  brokerId: string;
  brokerName?: string;
  bonusSummary?: BrokerBonusSummary;
  selectedModelId: string;
  setSelectedModelId: (value: string) => void;
}

export interface BrokerSummary {
  id: string;
  sfBrokerId?: string;
  sfCarrierId?: string;
  beginningBookSize: number;
  beginningLoc: number;
  previousYearBonus?: number;
  bonusGoal?: number;
  carrierRuleId?: string;
  trancheCode?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  carrier?: Carrier;
  carrierRuleTitle?: string;
  isCurrent?: boolean;
}

export interface CarrierRuleSelection {
  carrierRuleId: string;
  carrierId: string;
  ruleId: string;
  ruleTitle: string;
  ruleYear: number;
}

export interface BonusYearSettings {
  guardian?: GuardianBonusYearSettings;
  principal?: PrincipalBonusYearSettings;
  lincoln?: LincolnBonusYearSettings | LincolnBonusYearSettings[];
}

export type CarrierBonusYear =
  | LincolnBonusYearSettings
  | PrincipalBonusYearSettings
  | GuardianBonusYearSettings;

export interface GuardianBonusYearSettings {
  bonusYearLabel?: string;
  bonusYearStart?: string;
  bonusYearEnd?: string;
  bonusDescription?: string;
  termCutOff?: string;
  guardianExistingBizTermCutOff?: string;
  forecastMonths?: number;
  newBonusLevelOnePct?: number;
  newBonusLevelTwoPct?: number;
  newBonusLevelThreePct?: number;
  persistencyBonusLevelOnePct?: number;
  persistencyBonusLevelTwoPct?: number;
  persistencyBonusLevelThreePct?: number;
  spcBonusCap?: number;
  persistencyThresholds?: string;
  nbThresholds?: { [x: string]: number };
}

export interface PrincipalBonusYearSettings {
  bonusYearLabel?: string;
  bonusYearStart?: string;
  bonusYearEnd?: string;
  bonusDescription?: string;
  minLives?: number;
  minComm?: number;
  premCap?: number;
  persistencyThresholds?: string;
  persistencyPayouts?: string;
  nbPayouts?: string;
  nbThreshold?: string;
}

export interface LincolnBonusYearSettings {
  ruleId?: string;
  bonusYearLabel?: string;
  bonusYearStart?: string;
  bonusYearEnd?: string;
  bonusDescription?: string;
  newBizTermCutoff?: string;
  existingBizTermCutoff?: string;
  premCap?: number;
  trancheCode?: string;
  ifPremMin?: number;
  ifPremMinA?: number;
  ifPremMinB?: number;
  newBonusLevelOnePct?: number;
  newBonusLevelTwoPct?: number;
  newBonusLevelThreePct?: number;
  newBonusLevelFourPct?: number;
  persistencyBonusLevelOnePct?: number;
  persistencyBonusLevelTwoPct?: number;
  persistencyBonusLevelThreePct?: number;
  persistencyBonusLevelFourPct?: number;
  persistencyBonusLevelOnePctA?: number;
  persistencyBonusLevelTwoPctA?: number;
  persistencyBonusLevelThreePctA?: number;
  persistencyBonusLevelFourPctA?: number;
  persistencyBonusLevelOnePctB?: number;
  persistencyBonusLevelTwoPctB?: number;
  persistencyBonusLevelThreePctB?: number;
  persistencyBonusLevelFourPctB?: number;
  persistencyThresholds?: string;
  nbThresholds?: { [x: string]: number };
}

export interface LocCombinedApi {
  broker_id: string;
  carrier: string | null;
  group_name: string | null;
  group_number: string | null;
  status: string | null;
  status_date: string | null;
  annualized_premium: number | null;
  renewal_date: string | null;
  original_plan_sale_date: string | null;
  premium_eligible_date: string | null;
  contract_type: string | null;
  participants: number | null;
  broker_split: number | null;
  eligibility_status: string | null;
  new_bonus_level_one: number | null;
  new_bonus_level_two: number | null;
  new_bonus_level_three: number | null;
  persistency_bonus_level_one: number | null;
  persistency_bonus_level_two: number | null;
  persistency_bonus_level_three: number | null;
  bonus_year_classification: string | null;
  starting_premium: number | null;
  eligible_premium: number | null;
}

// Raw API shape for carrier bonus summary (snake_case from backend).
export interface CarrierBonusSummaryApi {
  broker_id: string;
  carrier_id: string;
  broker_name: string | null;
  carrier_name: string | null;
  carrier_update_date: string | null;
  persistency_percentage: number | null;
  persistency_tier_level: string | null;
  persistency_projected_bonus: number | null;
  new_business_premium: number | null;
  new_business_loc_credits: number | null;
  new_business_tier_level: string | null;
  new_business_bonus_amount: number | null;
  total_projected_bonus: number | null;
  persistency_start_book: number | null;
  term_prem: number | null;
  persistency: number | null;
  bonus_eligible_persistency_premium: number | null;
  bonus_eligible_new_business_premium: number | null;
  bonus_description: string | null;
}

// Raw API shape for bonus forecast charts (snake_case from backend).
export interface BonusForecastChartApi {
  broker_id: string;
  chart_type: string;
  carrier_name: string | null;
  value: number | null;
  percentage_of_total: number | null;
}

// Raw API shape for monthly progress rows (snake_case from backend).
export interface MonthlyProgressApi {
  broker_id: string;
  month_date: string | null;
  month_name: string | null;
  month_number: number | null;
  guardian_total_bonus: number | null;
  principal_total_bonus: number | null;
  lincoln_total_bonus: number | null;
  total_bonus: number | null;
  guardian_cumulative_bonus: number | null;
  principal_cumulative_bonus: number | null;
  lincoln_cumulative_bonus: number | null;
  total_cumulative_bonus: number | null;
}

// Raw API shape for monthly progress cards (snake_case from backend).
export interface MonthlyProgressCardsApi {
  broker_id: string;
  prior_year_bonus: number | null;
  goal: number | null;
  estimated_bonus: number | null;
  percent_to_target: number | null;
  bonus_growth_percentage: number | null;
  bonus_growth_amount: number | null;
}

export type ChartData = {
  name: string;
  [key: string]: number | string;
};

export type BrokerSummarySavePayload = {
  sfCarrierId: string;
  beginningBookSize: number;
  beginningLoc: number;
  previousYearBonus?: number;
  bonusGoal?: number | null;
  trancheCode?: string;
  carrierRuleId?: string;
  isCurrent: boolean;
};

export type CarrierSettingsMap = {
  guardian: GuardianBonusYearSettings;
  principal: PrincipalBonusYearSettings;
  lincoln: LincolnBonusYearSettings;
};

export type PersistencyPayout = {
  value: string;
  label: string;
};

export type InOutTreeRow = {
  dataPath: string[];
  annualized_premium: number | null;
  starting_premium: number | null;
  eligible_premium: number | null;
};

export type ScenarioDetail = {
  name: string;
  createdAt: string;
  groups: ScenarioLOC[];
};

export type Baseline = {
  id: string;
  sfBrokerId: string;
  label: string;
  createdAt: string;
  updatedAt: string;
};

export type BaselineCarrierBonusYear = {
  sfBrokerId: string;
  sfCarrierId: string;
  carrierRuleId: string | null;
  trancheCode: string | null;
  bonusYear: number | null;
};

export type BaselineDetail = {
  metadata: Baseline;
  locs: LOC[];
  locCount: number;
  carrierBonusYears: BaselineCarrierBonusYear[];
};

export type LOC = {
  id: string;
  createdAt: string;
  policyId?: string | null;
  clientName?: string | null;
  productNo?: string | null;
  policyNo?: string | null;
  policyType?: string | null;
  planType?: string | null;
  carrierName?: string | null;
  contractType?: string | null;
  contributionType?: string | null;
  participants?: number | null;
  brokerSplit?: number | null;
  originalPlanSaleDate?: string | null;
  premiumEligibleDate?: string | null;
  status?: string | null;
  statusReason?: string | null;
  statusDate?: string | null;
  effectiveDate?: string | null;
  renewalDate?: string | null;
  bonusEligibilityStatus?: string | null;
  averageMonthlyPremiumOverride?: number | null;
  newBusinessQualificationPremiumOverride?: number | null;
  persistencyQualificationStartOverride?: number | null;
  brokerName?: string | null;
  isBbeCarrier?: boolean | null;
  isActive?: boolean | null;
  brokerSk?: string | null;
  carrierSk?: string | null;
  clientSk?: string | null;
  updatedAt?: string | null;
  brokerId?: string | null;
  carrierId?: string | null;
  clientId?: string | null;
  baselineId: string;

  // scenario modeling summary data table fields
  carrierBonus: number;
  carrierData: string;
  nbBonus: number;
  nbBonusLevel: string;
  nbLocCredits: number;
  nbPremium: number;
  persistency: number;
  persistencyBonus: number;
  persistencyBonusLevel: string;
};

export type ScenarioSummary = {
  bobBonus: number;
  carriers: LOC[];
  id: string;
  isBaseline: boolean;
  name: string;
};

export enum ScenarioEditType {
  Migrate = "MIGRATE",
  Terminate = "TERMINATE",
  LostBOR = "LOST_BOR",
  AddNew = "ADD_NEW",
}

export enum ScenarioRowStatus {
  Terminated = "Terminated",
  LostBOR = "Lost BOR",
  Active = "Active",
}

export type ScenarioEdit = {
  id: string;
  scenarioId: string;
  editType: ScenarioEditType;
  sourceBaselineLocId: string | null;
  dateOfChange: string;
  migrateToCarrierId?: string;
  migrateToCarrierName?: string;
  newLoc?: ScenarioNewLocInput;
};

export type ScenarioNewLocInput = {
  carrierId: string;
  clientName: string;
  policyType: string;
  planType: string;
  originalPlanSaleDate: string;
  effectiveDate: string;
  premiumEligibleDate: string;
  averageMonthlyPremiumOverride: number;
  contractType: string;
  participants: number;
  brokerSplit: number;
};

export type ScenarioEditInput = {
  editType: ScenarioEditType;
  effectiveDate: string;
  sourceBaselineLocId?: string;
  newLoc?: ScenarioNewLocInput;
};

export type Scenario = {
  id: string;
  baselineId: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export type ScenarioLOC = Omit<LOC, "baselineId"> & {
  scenarioId: string;
  editId?: string | null;
  migrateToCarrierName?: string | null;
  migrateFromCarrierName?: string | null;
  scenarioChange?: ScenarioEditType | null;
};

export type ScenarioDetailRow = Omit<LOC, "baselineId"> & {
  scenarioId: string;
  editId?: string | null;
  scenarioChange: ScenarioEditType | null;
  migrateToCarrierName?: string | null;
  migrateFromCarrierName?: string | null;
};
